import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { downloadProtectedFile } from "@/lib/fileAccess";
import { useCompanyFilter } from "@/lib/companyFilter";
import { Check, X, Upload, Download } from "lucide-react";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-rose-100 text-rose-700",
};

function currentFY() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

export default function TaxAdmin() {
  const { companyId, companyOptions, setCompanyId, empParams } = useCompanyFilter();
  const [tab, setTab] = useState("declarations");
  const [decls, setDecls] = useState([]);
  const [form16, setForm16] = useState([]);
  const [emps, setEmps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [upload, setUpload] = useState({ employee_id: "", financial_year: currentFY(), file: null });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const loadDecls = async () => {
    const r = await api.get("/tax/declarations", {
      params: statusFilter ? { status: statusFilter } : {},
    });
    setDecls(r.data || []);
  };
  const loadForm16 = async () => {
    const r = await api.get("/tax/form16");
    setForm16(r.data || []);
  };

  useEffect(() => {
    api.get("/employees", { params: empParams })
      .then((r) => setEmps((r.data || []).map((e) => ({
        id: e.id,
        code: e.basic?.code || "",
        name: [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" "),
      }))))
      .catch(() => setEmps([]));
  }, [companyId, empParams]);

  const filteredDecls = useMemo(() => {
    if (!companyId || companyId === "all") return decls;
    const ids = new Set(emps.map((e) => e.id));
    return decls.filter((d) => ids.has(d.employee_id));
  }, [decls, emps, companyId]);

  const filteredForm16 = useMemo(() => {
    if (!companyId || companyId === "all") return form16;
    const ids = new Set(emps.map((e) => e.id));
    return form16.filter((f) => ids.has(f.employee_id));
  }, [form16, emps, companyId]);

  useEffect(() => {
    if (tab === "declarations") loadDecls().catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
    else loadForm16().catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter]);

  const decide = async (id, action) => {
    const note = action === "Rejected" ? (window.prompt("Rejection note (optional)") || "") : "";
    try {
      await api.post(`/tax/declarations/${id}/decision`, { action, note });
      setMsg(`Declaration ${action.toLowerCase()}.`);
      await loadDecls();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Decision failed");
    }
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!upload.file || !upload.employee_id) {
      setErr("Select employee and file");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("employee_id", upload.employee_id);
      fd.append("financial_year", upload.financial_year);
      fd.append("file", upload.file);
      await api.post("/tax/form16", fd);
      setMsg("Form 16 uploaded.");
      setUpload({ ...upload, file: null });
      await loadForm16();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const money = (n) => Number(n || 0).toLocaleString("en-IN");

  return (
    <AdminLayout title="Tax & Form 16" breadcrumbs={["Home", "Payroll", "Tax"]}>
      {err && <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
      {msg && <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{msg}</div>}

      <div className="mb-3">
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Company</label>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="border border-slate-200 rounded px-3 py-1.5 text-sm min-w-[200px]"
        >
          {companyOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {[
          { id: "declarations", label: "12BB Declarations" },
          { id: "form16", label: "Form 16 uploads" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? "border-[#226DB4] text-[#226DB4]" : "border-transparent text-slate-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "declarations" && (
        <>
          <div className="mb-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              {["Pending", "Approved", "Rejected"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="bg-white border border-slate-200 rounded shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Employee</th>
                  <th className="text-left px-4 py-2.5">FY</th>
                  <th className="text-right px-4 py-2.5">80C</th>
                  <th className="text-right px-4 py-2.5">80D</th>
                  <th className="text-right px-4 py-2.5">HRA</th>
                  <th className="text-right px-4 py-2.5">Home loan</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDecls.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No declarations</td></tr>
                ) : filteredDecls.map((d) => (
                  <tr key={d.id}>
                    <td className="px-4 py-2.5 font-medium">{d.employee_name || "—"}</td>
                    <td className="px-4 py-2.5">{d.financial_year}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.section_80c)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.section_80d)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.hra)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.home_loan_interest)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs ${STATUS_COLORS[d.status] || ""}`}>{d.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {d.status === "Pending" && (
                        <div className="inline-flex gap-1">
                          <button type="button" onClick={() => decide(d.id, "Approved")}
                            className="p-1.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50" title="Approve">
                            <Check className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => decide(d.id, "Rejected")}
                            className="p-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50" title="Reject">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "form16" && (
        <>
          <form onSubmit={doUpload} className="bg-white border border-slate-200 rounded shadow-sm p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <label className="text-sm sm:col-span-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Employee</span>
              <select
                required
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm mt-0.5"
                value={upload.employee_id}
                onChange={(e) => setUpload({ ...upload, employee_id: e.target.value })}
              >
                <option value="">Select…</option>
                {emps.map((e) => (
                  <option key={e.id} value={e.id}>
                    {(e.code || "")} — {e.name || ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">FY</span>
              <input
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm mt-0.5"
                value={upload.financial_year}
                onChange={(e) => setUpload({ ...upload, financial_year: e.target.value })}
                placeholder="2025-2026"
              />
            </label>
            <label className="text-sm">
              <span className="text-xs font-semibold text-slate-500 uppercase">PDF</span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="w-full text-sm mt-0.5"
                onChange={(e) => setUpload({ ...upload, file: e.target.files?.[0] || null })}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded bg-[#226DB4] hover:bg-[#1B5A94] text-white disabled:opacity-60 sm:col-span-4 sm:w-auto sm:justify-self-start"
            >
              <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload Form 16"}
            </button>
          </form>

          <div className="bg-white border border-slate-200 rounded shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Employee</th>
                  <th className="text-left px-4 py-2.5">FY</th>
                  <th className="text-left px-4 py-2.5">File</th>
                  <th className="text-left px-4 py-2.5">Uploaded</th>
                  <th className="text-right px-4 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredForm16.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No Form 16 files yet</td></tr>
                ) : filteredForm16.map((f) => (
                  <tr key={f.id}>
                    <td className="px-4 py-2.5 font-medium">{f.employee_name || "—"}</td>
                    <td className="px-4 py-2.5">{f.financial_year}</td>
                    <td className="px-4 py-2.5 text-slate-600">{f.file_name}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : "—"}
                      {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => downloadProtectedFile(f.download_url || `/api/tax/form16/${f.id}/download`, f.file_name)}
                        className="inline-flex items-center gap-1 text-xs text-[#226DB4] hover:underline"
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
